import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@bi-studio/shared/hooks';
import { AppLayout, LoadingScreen, ErrorState, ConfirmationModal } from '@bi-studio/shared/components';
import { notifications } from '@mantine/notifications';
import { createForm } from '@bi-studio/shared/apis';
import type { FormField } from '@bi-studio/shared/lib';
import { PageContainer, PageActions, FormCreateActionsButton } from '@/components';
import { FormCreateView } from '@/components/forms/FormCreateView';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { 
    fetchListDataSources, 
    fetchTableSchema, 
    selectFormSources, 
    selectFormTables, 
    selectLoadingSources, 
    selectLoadingTables,
    resetFormState
} from '@/store/slices/formSlice';
import {
    setFormDraft,
    selectFormDraft
} from '@/store/slices/draftSlice';
import { FormInput } from '@bi-studio/shared/lib';

export default function CreateFormPage() {
    const { token, isLoading, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const folderId = searchParams.get('folderId');
    const formDraft = useAppSelector(selectFormDraft);

    const dataSources = useAppSelector(selectFormSources);
    const tables = useAppSelector(selectFormTables);
    const loadingDataSources = useAppSelector(selectLoadingSources);
    const loadingTables = useAppSelector(selectLoadingTables);

    const handleLogout = () => {
        navigate("/login");
    };

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            navigate("/login");
        }
    }, [isLoading, isAuthenticated, navigate]);

    useEffect(() => {
        return () => {
            dispatch(resetFormState());
        };
    }, [dispatch]);

    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<string | null>("general");
    const [selectedDataSource, setSelectedDataSource] = useState<string | null>(null);
    const [selectedTable, setSelectedTable] = useState<string | null>(null);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [backgroundImage, setBackgroundImage] = useState('');
    const [backgroundColor, setBackgroundColor] = useState('');
    const [isPublic, setIsPublic] = useState(false);
    const [rateLimitEnabled, setRateLimitEnabled] = useState(false);
    const [rateLimitCount, setRateLimitCount] = useState(5);
    const [rateLimitWindow, setRateLimitWindow] = useState(60);
    const [fields, setFields] = useState<FormField[]>([]);
    const [allowedRoles, setAllowedRoles] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);

    // Draft Restoration
    useEffect(() => {
        if (formDraft && !name && !selectedDataSource) {
            setIsRestoreModalOpen(true);
        }
    }, [formDraft]);

    const handleRestoreDraft = () => {
        const draft = structuredClone(formDraft) as FormInput;
        setName(draft.name || '');
        setDescription(draft.description || '');
        setSelectedDataSource(draft.dataSourceId || null);
        setSelectedTable(draft.tableName || null);
        setFields(draft.fields || []);
        setAllowedRoles(draft.allowedRoles || []);
        if (draft.config) {
            setBackgroundImage((draft.config.backgroundImage as string) || '');
            setBackgroundColor((draft.config.backgroundColor as string) || '');
            setIsPublic((draft.config.isPublic as boolean) || false);
            if (draft.config.rateLimit) {
                const rl = draft.config.rateLimit as any;
                setRateLimitEnabled(rl.enabled || false);
                setRateLimitCount(rl.limit || 5);
                setRateLimitWindow(rl.window || 60);
            }
        }
        setIsRestoreModalOpen(false);
    };

    const handleDiscardDraft = () => {
        dispatch(setFormDraft(null));
        setIsRestoreModalOpen(false);
    };

    // Auto-save Draft
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (name || selectedDataSource || fields.length > 0) {
                const draft: FormInput = {
                    name,
                    description,
                    dataSourceId: selectedDataSource || '',
                    tableName: selectedTable || '',
                    fields,
                    allowedRoles,
                    config: {
                        backgroundImage,
                        backgroundColor,
                        isPublic,
                        rateLimit: rateLimitEnabled ? {
                            enabled: true,
                            limit: rateLimitCount,
                            window: rateLimitWindow
                        } : undefined
                    }
                };
                dispatch(setFormDraft(draft));
            }
        }, 2000);
        return () => clearTimeout(timeoutId);
    }, [name, description, selectedDataSource, selectedTable, fields, allowedRoles, backgroundImage, backgroundColor, isPublic, rateLimitEnabled, rateLimitCount, rateLimitWindow, dispatch]);

    useEffect(() => {
        if (token) {
            dispatch(fetchListDataSources(token));
        }
    }, [token, dispatch]);

    useEffect(() => {
        if (token && selectedDataSource) {
            dispatch(fetchTableSchema({ token, dataSourceId: selectedDataSource }));
        }
    }, [token, selectedDataSource, dispatch]);


    useEffect(() => {
        if (selectedTable) {
            const table = tables.find((t) => t.name === selectedTable);
            if (table && table.columnDefinitions) {
                const autoFields: FormField[] = table.columnDefinitions.map((col, idx) => ({
                    name: col.name,
                    label: col.name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                    type: col.dataType.includes('int') || col.dataType.includes('num') || col.dataType.includes('decimal') ? 'number' :
                        col.dataType.includes('bool') ? 'checkbox' :
                            col.dataType.includes('date') || col.dataType.includes('time') ? 'date' : 'text',
                    required: !col.isNullable && col.defaultValue === null,
                    order: idx,
                }));
                setFields(autoFields);
                if (!name) setName(selectedTable + ' Form');
            }
        }
    }, [selectedTable, tables]);

    const handleCreate = async () => {
        if (!selectedDataSource || !selectedTable || !name) {
            notifications.show({
                title: 'Error',
                message: 'Please fill in all required fields (Data Source, Table, Form Name)',
                color: 'red',
            });
            return;
        }

        if (fields.length === 0) {
            notifications.show({
                title: 'Error',
                message: 'Please add at least one field to the form',
                color: 'red',
            });
            return;
        }

        const invalidField = fields.find(f => !f.name.trim() || !f.label.trim());
        if (invalidField) {
            notifications.show({
                title: 'Error',
                message: 'All fields must have a name and label',
                color: 'red',
            });
            return;
        }

        if (!token) return;

        setLoading(true);
        try {
            await createForm(
                token,
                {
                    name,
                    description,
                    dataSourceId: selectedDataSource,
                    tableName: selectedTable,
                    fields,
                    config: {
                        backgroundImage,
                        backgroundColor,
                        isPublic,
                        rateLimit: rateLimitEnabled ? {
                            enabled: true,
                            limit: rateLimitCount,
                            window: rateLimitWindow
                        } : undefined
                    },
                    allowedRoles,
                },
                folderId || undefined
            );
            dispatch(setFormDraft(null)); // Clear draft on success
            notifications.show({
                title: 'Success',
                message: 'Form created successfully',
                color: 'green',
            });
            if (folderId && folderId !== 'root') {
                navigate(`/folders/${folderId}`);
            } else {
                navigate('/folders');
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to create form');
        } finally {
            setLoading(false);
        }
    };

    const updateField = (index: number, updates: Partial<FormField>) => {
        const newFields = [...fields];
        newFields[index] = { ...newFields[index], ...updates };
        setFields(newFields);
    };

    const removeField = (index: number) => {
        setFields(fields.filter((_, i) => i !== index));
    };

    const handleCancel = () => {
        if (folderId && folderId !== "root") {
            navigate(`/folders/${folderId}`);
        } else {
            navigate("/folders");
        }
    };

    const handleBack = handleCancel;

    if (isLoading) {
        return (
            <AppLayout onLogout={handleLogout} title="Workspace" subtitle="BI Studio">
                <LoadingScreen message="Initializing workspace..." />
            </AppLayout>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    return (
        <AppLayout
            title="Workspace"
            subtitle="BI Studio"
            onLogout={handleLogout}
        >
            <PageContainer>
                <PageActions 
                    title="Create Form" 
                    onBack={handleBack}
                >
                    <FormCreateActionsButton
                        handleCancel={handleCancel}
                        handleSave={handleCreate}
                        isSaving={loading}
                    />
                </PageActions>

                {error && (
                    <ErrorState 
                        title="Failed to create form"
                        message={error || undefined}
                        onAction={() => setError(null)}
                    />
                )}

                <FormCreateView
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    name={name}
                    setName={setName}
                    description={description}
                    setDescription={setDescription}
                    backgroundImage={backgroundImage}
                    setBackgroundImage={setBackgroundImage}
                    backgroundColor={backgroundColor}
                    setBackgroundColor={setBackgroundColor}
                    isPublic={isPublic}
                    setIsPublic={setIsPublic}
                    rateLimitEnabled={rateLimitEnabled}
                    setRateLimitEnabled={setRateLimitEnabled}
                    rateLimitCount={rateLimitCount}
                    setRateLimitCount={setRateLimitCount}
                    rateLimitWindow={rateLimitWindow}
                    setRateLimitWindow={setRateLimitWindow}
                    fields={fields}
                    setFields={setFields}
                    allowedRoles={allowedRoles}
                    setAllowedRoles={setAllowedRoles}
                    dataSources={dataSources}
                    selectedDataSource={selectedDataSource}
                    setSelectedDataSource={setSelectedDataSource}
                    tables={tables}
                    selectedTable={selectedTable}
                    setSelectedTable={setSelectedTable}
                    loadingDataSources={loadingDataSources}
                    loadingTables={loadingTables}
                    isSaving={loading}
                    updateField={updateField}
                    removeField={removeField}
                />

                <ConfirmationModal
                    opened={isRestoreModalOpen}
                    onClose={handleDiscardDraft}
                    onConfirm={handleRestoreDraft}
                    title="Unsaved Draft Found"
                    confirmLabel="Restore"
                    cancelLabel="Discard"
                >
                    You have an unsaved form draft. Would you like to restore it?
                </ConfirmationModal>
            </PageContainer>
        </AppLayout>
    );
}
